import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const BEAT_1_OUT_AT = 0.25;
const BEAT_2_AT = 0.38;

const FAN = [
    {
        from: { x: 0, y: 0.75, rotate: -3, scale: 0.88 },
        to: { x: -0.25, y: -0.25, rotate: 6, scale: 1 },
        parallax: 0.16,
    },
    {
        from: { x: 0, y: 0.75, rotate: 0, scale: 0.88 },
        to: { x: 0.28, y: -0.27, rotate: -7, scale: 1 },
        parallax: 0.15,
    },
    {
        from: { x: 0, y: 0.75, rotate: 2, scale: 0.88 },
        to: { x: -0.36, y: 0.24, rotate: -5, scale: 1 },
        parallax: 0.09,
    },
    {
        from: { x: 0, y: 0.75, rotate: -2, scale: 0.88 },
        to: { x: 0, y: 0.35, rotate: 4, scale: 1 },
        parallax: 0.2,
    },
    {
        from: { x: 0, y: 0.75, rotate: 3, scale: 0.88 },
        to: { x: 0.38, y: 0.24, rotate: -4, scale: 1 },
        parallax: 0.11,
    },
];

export function heroInspo() {
    const $comp = document.querySelector('.component--hero-inspo');
    if (!$comp) return;

    const $stickyWrap = $comp.querySelector('.hero-inspo__sticky-wrap');
    const $beat1 = $comp.querySelector('.hero-inspo__beat--1');
    const $beat2 = $comp.querySelector('.hero-inspo__beat--2');
    const $figureWrap = $comp.querySelector('.hero-inspo__figure-wrap');
    const $figure = $comp.querySelector('.hero-inspo__figure');
    const $tracks = gsap.utils.toArray($comp.querySelectorAll('.hero-inspo__beat-2-img-track'));
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!$stickyWrap) {
        $comp.classList.add('is-ready');
        $beat1?.classList.add('is-in', 'is-active');
        return;
    }

    let introComplete = reduceMotion;

    const fanVars = (i, key) => {
        const pos = FAN[i][key];
        const xAmp = window.innerWidth < 760 ? 1 : 1;
        return {
            x: pos.x * window.innerWidth * xAmp,
            y: pos.y * window.innerHeight,
            rotate: pos.rotate,
            scale: pos.scale,
        };
    };

    const setBeatClasses = (progress) => {
        const beat1Out = progress >= BEAT_1_OUT_AT;
        const inBeat2 = progress >= BEAT_2_AT;

        const setBeatState = ($el, isActive) => {
            if (!$el) return;
            $el.classList.toggle('is-active', isActive);
            $el.classList.toggle('is-out', !isActive);
        };

        if (introComplete) {
            $beat1?.classList.add('is-in');
            setBeatState($beat1, !beat1Out);
        }

        if (inBeat2) {
            $beat2?.classList.add('is-in');
        }
        if ($beat2?.classList.contains('is-in')) {
            setBeatState($beat2, inBeat2);
        }

        $comp.classList.toggle('is-beat-1', introComplete && !inBeat2);
        $comp.classList.toggle('is-beat-2', inBeat2);
    };

    $tracks.forEach(($track, i) => {
        gsap.set($track, {
            xPercent: -50,
            yPercent: -50,
            ...fanVars(i, 'from'),
            zIndex: 5 - Math.abs(i - 2),
            transformOrigin: '50% 50%',
        });
    });

    if ($figure) {
        gsap.set($figure, { scale: reduceMotion ? 1 : 1.1, transformOrigin: '50% 50%' });
    }

    const scene = {
        trigger: $stickyWrap,
        start: 'top top',
        end: 'bottom bottom',
        scrub: reduceMotion ? false : 0.45,
        invalidateOnRefresh: true,
    };

    const mainTrigger = ScrollTrigger.create({
        ...scene,
        onUpdate: (self) => setBeatClasses(self.progress),
        onRefresh: (self) => setBeatClasses(self.progress),
    });

    if (!reduceMotion) {
        if ($figureWrap) {
            const setBlur = (progress) => {
                $figureWrap.style.setProperty('--hero-inspo-blur', (progress * 10).toFixed(2));
            };

            ScrollTrigger.create({
                trigger: $stickyWrap,
                start: '10% top',
                end: '35% top',
                scrub: 0.45,
                invalidateOnRefresh: true,
                onUpdate: (self) => setBlur(self.progress),
                onRefresh: (self) => setBlur(self.progress),
            });
        }

        $tracks.forEach(($track, i) => {
            const $img = $track.querySelector('figure');

            gsap.fromTo($track, {
                x: () => fanVars(i, 'from').x,
                y: () => fanVars(i, 'from').y,
                rotate: () => fanVars(i, 'from').rotate,
                scale: () => fanVars(i, 'from').scale,
            }, {
                x: () => fanVars(i, 'to').x,
                y: () => fanVars(i, 'to').y,
                rotate: () => fanVars(i, 'to').rotate,
                scale: () => fanVars(i, 'to').scale,
                ease: 'none',
                immediateRender: true,
                    scrollTrigger: {
                        trigger: $stickyWrap,
                        start: '30% top',
                        end: 'bottom bottom',
                        scrub: 0.45,
                        invalidateOnRefresh: true,
                    },
            });

            if ($img) {
                gsap.fromTo($img, {
                    y: 0,
                }, {
                    y: () => (FAN[i].parallax || 0) * window.innerHeight,
                    ease: 'none',
                    immediateRender: true,
                    overwrite: false,
                    scrollTrigger: {
                        trigger: $stickyWrap,
                        start: 'bottom bottom',
                        end: 'bottom top',
                        scrub: 0.45,
                        invalidateOnRefresh: true,
                    },
                });
            }
        });
    } else {
        $tracks.forEach(($track, i) => {
            gsap.set($track, fanVars(i, 'to'));
        });
    }

    const skipIntro = mainTrigger.progress > 0.02;

    if (reduceMotion || skipIntro) {
        introComplete = true;
        if ($figure) gsap.set($figure, { scale: 1 });
        setBeatClasses(mainTrigger.progress);
    } else {
        const activateBeat1 = () => {
            introComplete = true;
            $beat1?.classList.add('is-in', 'is-active');
            $comp.classList.add('is-beat-1');
        };

        if ($figure) {
            gsap.to($figure, {
                scale: 1,
                duration: 1.8,
                ease: 'power2.out',
                delay: 0.12,
                onStart: activateBeat1,
            });
        } else {
            gsap.delayedCall(0.2, activateBeat1);
        }
    }

    $comp.classList.add('is-ready');
}
